/**
 * Visibility rules for the language / model-type prompt.
 *
 * The prompt asks whoever has
 * `provider_settings.settings.language_model_popup.show_on_login` set to true.
 * New doctors start with it raised. Answering or dismissing the prompt lowers it.
 *
 * Every condition resolves to a named state rather than a bare false, because a
 * prompt that is hidden for the wrong reason is otherwise invisible to debug: it
 * sits behind a feature gate, two fetches, and the guide.
 */

export interface LanguagePromptVisibilityInput {
  /** Standalone (embedded EHR launch) sessions never see onboarding UI. */
  isStandaloneUser: boolean;
  /** Login / OAuth callback routes, where no modal should appear. */
  isOnAuthRoute: boolean;
  /** Amplitude `model-type-selector` gate — the same gate as the Settings selector. */
  hasModelTypeFeature: boolean;
  /** Provider attestation check is still in flight. */
  isAttestationLoading: boolean;
  /** Provider attestation is known to be required and outstanding. */
  isAttestationBlocking: boolean;
  /** A tutorial tooltip is currently on screen (live state). */
  isTutorialActive: boolean;
  /**
   * The guide's progress is still loading or it is queued to auto-start.
   * `isActive` is false in both cases, so this keeps the prompt from flashing up
   * right before the guide opens.
   */
  isTutorialPending: boolean;
  /**
   * `settings.language_model_popup.show_on_login`. Undefined while loading, when
   * the key is absent, or when the fetch failed.
   */
  showOnLoginFlag: boolean | undefined;
}

export type LanguagePromptState =
  | "show"
  | "standalone-user"
  | "auth-route"
  | "feature-gate-off"
  | "attestation-loading"
  | "attestation-outstanding"
  | "guide-on-screen"
  | "guide-pending"
  | "flag-not-set";

/** The single decision, as a named state so it can be logged when hidden. */
export function resolveLanguagePromptState(
  input: LanguagePromptVisibilityInput
): LanguagePromptState {
  if (input.isStandaloneUser) return "standalone-user";
  if (input.isOnAuthRoute) return "auth-route";
  if (!input.hasModelTypeFeature) return "feature-gate-off";

  // Provider attestation is a compliance gate with its own modal and can also
  // fire on a first login, so it goes first. Only a positive answer blocks: a
  // failed check must not silently suppress the prompt forever.
  if (input.isAttestationLoading) return "attestation-loading";
  if (input.isAttestationBlocking) return "attestation-outstanding";

  if (input.isTutorialActive) return "guide-on-screen";
  if (input.isTutorialPending) return "guide-pending";

  if (input.showOnLoginFlag !== true) return "flag-not-set";

  return "show";
}

export function shouldShowLanguagePrompt(input: LanguagePromptVisibilityInput): boolean {
  return resolveLanguagePromptState(input) === "show";
}

export default shouldShowLanguagePrompt;
